import { l } from '@manychat/manyui'

import { alert } from 'common/core'
import { IAsyncThunkActionWithReturnValue, IThunkAction } from 'common/core/interfaces/actions'
import { CreateFolderResponse } from 'common/core/interfaces/folder'
import { globalFieldFoldersSlice } from 'common/fields/reducers/globalFieldFolders'
import { getIsFolderNameExists } from 'common/fields/selectors/globalFieldFoldersSelectors'
import { GlobalFieldsActions } from 'common/fields/store/globalFields'
import { handleError } from 'shared/api/lib/errors/handlers'
import { FoldersApi } from 'shared/api/requests/folders'
import { getClientId } from 'utils/clientId'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

const FOLDER_ENTITY_GAFS = 30
const ROOT_PATH = '/'

export const fetchGlobalFieldFolders = () =>
  globalFieldFoldersSlice.actions.getListFx({
    payload: { body: { entity: FOLDER_ENTITY_GAFS, path: ROOT_PATH } },
  })

export const createGlobalFieldFolder =
  (title: string): IThunkAction<Promise<CreateFolderResponse | undefined>> =>
  async (dispatch, getState) => {
    const isFolderNameExists = getIsFolderNameExists(getState(), title)

    if (isFolderNameExists) {
      alert(l.translate('Folder with this name already exists'), 'danger')
      return
    }

    try {
      const { data } = await FoldersApi.create({
        body: { title, entity: FOLDER_ENTITY_GAFS, path: ROOT_PATH },
        query: { client_id: getClientId() },
      })
      dispatch(globalFieldFoldersSlice.actions.appendItem({ item: data.folder }))
      alert(l.translate('Folder created'), 'success')
      return data
    } catch (error) {
      handleError(error)
      return
    }
  }

export const createFolderAndMoveFields = (title: string, fieldIds: number[]): IThunkAction => {
  return async (dispatch, getState) => {
    const isFolderNameExists = getIsFolderNameExists(getState(), title)

    if (isFolderNameExists) {
      alert(l.translate('Folder with this name already exists'), 'danger')
      return
    }

    const result = await dispatch(createGlobalFieldFolder(title))
    if (!result) return
    const { folder } = result
    await dispatch(GlobalFieldsActions.bulkMoveFx({ fieldIds, folderId: folder.folder_id }))
  }
}

export const removeGlobalFieldFolder =
  (
    folderId: number,
    options: {
      path: string
      delete_content: boolean
    },
  ): IThunkAction =>
  async (dispatch) => {
    try {
      await FoldersApi.delete({ body: options, query: { client_id: getClientId() } })
      dispatch(globalFieldFoldersSlice.actions.deleteItem({ id: folderId }))
      alert(l.translate('Folder deleted!'), 'success')
      dispatch(
        GlobalFieldsActions.getListFx({
          payload: {
            query: {
              active_only: false,
              path: '/',
            },
          },
          effectConfig: { forceRequest: true },
        }),
      )
    } catch (error) {
      handleError(error)
    }
  }

export const updateGlobalFieldFolderName =
  (folderId: number, path: string, title: string): IAsyncThunkActionWithReturnValue<boolean> =>
  async (dispatch, getState) => {
    const isFolderNameExists = getIsFolderNameExists(getState(), title)

    if (isFolderNameExists) {
      alert(l.translate('Folder with this name already exists'), 'danger')
      return false
    }

    try {
      await FoldersApi.rename({ body: { path, title }, query: { client_id: getClientId() } })
      dispatch(globalFieldFoldersSlice.actions.updateItem({ id: folderId, data: { title } }))
      alert(l.translate('Folder renamed!'), 'success')
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

anotherTabNotificationsListener.on('folder_created', (data, dispatch) => {
  if (data.folder.entity === FOLDER_ENTITY_GAFS) {
    dispatch(globalFieldFoldersSlice.actions.appendItem({ item: data.folder }))
  }
})

anotherTabNotificationsListener.on('folder_renamed', (data, dispatch) => {
  if (data.folder.entity === FOLDER_ENTITY_GAFS) {
    dispatch(
      globalFieldFoldersSlice.actions.updateItem({
        id: data.folder.folder_id,
        data: data.folder,
      }),
    )
  }
})

anotherTabNotificationsListener.on('folder_deleted', (data, dispatch) => {
  if (data.folder) {
    if (data.folder.entity === FOLDER_ENTITY_GAFS) {
      dispatch(globalFieldFoldersSlice.actions.deleteItem({ id: data.folder.folder_id }))
    }
  }
})
